import { DateTime } from 'luxon';
import TicketPrices from 'src/utils/ticketPrices';
import ImageChooser from 'src/utils/imageChooser';
import EventIds from 'src/utils/EventIds';
import { embeddedYoutubeUrl, embeddedVimeoUrl } from 'src/utils/videoEmbed';
import StructuredData from 'src/utils/StructuredData';
import activeNetworkPlaceholder from 'src/assets/images/active_network_placeholder.jpeg';
import evvntPlaceholderImage from 'src/assets/images/placeholder-event-image.jpg';

const EVVNT_MAIN_CALENDAR_URL = 'https://evvnt.com/events/';
const EVVNT_TM_AFFILIATE_ID = 374335;

export default class Event {
  constructor(data, publisherSettings) {
    this.data = data;
    this.publisherSettings = publisherSettings;
    if (this.mainSource === 'evvnt' && this.data.links) {
      Object.keys(this.data.links).forEach((key) => {
        if (!this.data.links[key].match(/\?pid=/)) {
          this.data.links[key] += '?pid=' + publisherSettings.publisher.id;
        }
      });
    }
  }

  get id() {
    return this.data.objectID;
  }

  get sourceId() {
    return this.data.source_id_s;
  }

  get isExpired() {
    const unixSeconds = new Date().getTime() / 1000;

    if (this.hasEndTime) {
      return this.data.end_time_i < unixSeconds;
    }

    if (this.data.start_time_i) {
      return this.data.start_time_i < unixSeconds;
    }

    return false; // We don't know if it's expired or not
  }

  get parentId() {
    return this.data.event_parent_id;
  }

  get latitude() {
    return this.data.venue.latitude;
  }

  get longitude() {
    return this.data.venue.longitude;
  }

  get title() {
    return this.data.title;
  }

  get description() {
    if (this.data.description) {
      return [...this.data.description].slice(0, 300).join('');
    }
  }

  get summary() {
    return this.data.summary?.trim();
  }

  get fullDescription() {
    return this.data.description?.trim();
  }

  get hashtag() {
    return this.data.hashtag;
  }

  get virtual() {
    return this.data.online_only;
  }

  get artists() {
    if (!this.data.artists) {
      return;
    }
    return this.data.artists.split(/,\s+/);
  }

  get fullDescriptionWithoutSummary() {
    if (!this.fullDescription) {
      return;
    }

    if (this.fullDescription.startsWith(this.summary)) {
      return this.data.description.replace(this.summary, '').trim();
    }
    return this.fullDescription;
  }

  get createdAt() {
    // The timezone is inferred from the datetime field.
    // In Algolia the created_time is stored as an integer, so we need to convert it to a date.

    const timezone = this.datetime ? DateTime.fromISO(this.datetime, { setZone: true} ).zoneName : 'UTC';

    if (this.data.created_time_i) {
      return DateTime.fromSeconds(this.data.created_time_i, { zone: timezone }).toISO();
    }

    return null;
  }

  get isOnNow() {
    const unixSeconds = new Date().getTime() / 1000;

    return (
      this.hasEndTime &&
      this.data.start_time_i < unixSeconds &&
      this.data.end_time_i > unixSeconds
    );
  }

  get multiDay() {
    return this.hasEndTime && this.data.end_time_i - this.data.start_time_i > 60 * 60 * 24;
  }

  get datetime() {
    return this.data.start_time;
  }

  get endTime() {
    return this.data.end_time;
  }

  get venueAddressLines() {
    return [
      this.data.venue.address_1,
      this.data.venue.address_2,
      this.data.venue.town,
      this.data.venue.post_code,
    ].filter((e) => e && e.length);
  }

  get venueAddress() {
    this.venueAddressLines.join(', ');
  }

  get town() {
    return this.data.venue.town;
  }

  get country() {
    return this.data.venue.country;
  }

  get countryIsoCode() {
    return this.data.country.iso_code;
  }

  get images() {
    this.imagesMem = this.imagesMem || new ImageChooser(this.data.images).map_images();
    return this.imagesMem;
  }

  get defaultImage() {
    if (this.mainSource === 'active_network') {
      return activeNetworkPlaceholder;
    } else {
      return evvntPlaceholderImage;
    }
  }

  get imageUrl() {
    if (this.data.images && this.images.featured_thumb_url) {
      return this.images.featured_thumb_url;
    }

    return this.defaultImage;
  }

  get imageThumbUrl() {
    if (this.data.images && this.images.image_thumb_url) {
      return this.images.image_thumb_url;
    }

    return this.defaultImage;
  }

  get imagePlaceholderUrl() {
    if (this.data.images && this.images.list_thumb_url) {
      return this.images.list_thumb_url;
    }

    return this.imageThumbUrl;
  }

  get fullImageUrl() {
    if (this.data.images && this.images.image_full_url) {
      return this.images.image_full_url;
    }

    return this.defaultImage;
  }

  get imageUrls() {
    if (!this.data.images) {
      return [];
    }

    return new ImageChooser(this.data.images).images.map((i) => i?.original.url);
  }

  get venueName() {
    return this.data.venue.name;
  }

  get dateTimeDesc() {
    return this.timeWithDay(this.datetime);
  }

  get timeDesc() {
    return this.timeOnly(this.datetime);
  }

  get dateDesc() {
    return DateTime.fromISO(this.datetime, { setZone: true }).toFormat('LLL d');
  }

  get fullDateTimeDesc() {
    return this.timeWithDate(this.datetime);
  }

  get endDateDesc() {
    return DateTime.fromISO(this.data.end_time, { setZone: true }).toFormat('LLL d');
  }

  timeWithDate(isoDateTime, { addYear = false } = {}) {
    const format = addYear ? "LLL d, yyyy 'at' h:mma" : "LLL d 'at' h:mma";
    return DateTime.fromISO(isoDateTime, { setZone: true }).toFormat(format);
  }

  timeWithDay(isoDateTime) {
    return DateTime.fromISO(isoDateTime, { setZone: true }).toFormat("ccc 'at' h:mma");
  }

  timeOnly(isoDateTime) {
    return DateTime.fromISO(isoDateTime, { setZone: true }).toFormat('h:mma');
  }

  get dateTimeRangeDesc() {
    const startDate = DateTime.fromISO(this.datetime);
    const startDateInFutureYear = startDate.year > DateTime.local().year;
    const formattedStartDate = this.timeWithDate(this.datetime, {
      addYear: startDateInFutureYear,
    });

    const endDate = DateTime.fromISO(this.data.end_time);
    const endDateInFutureYear = endDate.year > DateTime.local().year;
    const formattedEndDate = this.timeWithDate(this.data.end_time, {
      addYear: endDateInFutureYear && !startDateInFutureYear,
    });

    if (!this.hasEndTime) {
      return formattedStartDate;
    }

    if (this.multiDay) {
      return `${formattedStartDate} - ${formattedEndDate}`;
    }

    // defaults to same day
    return `${formattedStartDate} - ${this.timeOnly(this.data.end_time)}`;
  }

  get hasEndTime() {
    const sourcesWithoutEndTimes = ['ticketmaster', 'goldstar', 'axs'];
    for (let i = 0; i < sourcesWithoutEndTimes.length; i++) {
      if (this.data.sources.includes(sourcesWithoutEndTimes[i])) {
        return false;
      }
    }
    return true;
  }

  get hasPrices() {
    return (
      !!this.data.prices && !!Object.keys(this.data.prices).length && !!this.ticketPriceRange
    );
  }

  get ticketPriceRange() {
    try {
      return new TicketPrices(this.data.prices).priceRange();
    } catch {
      return undefined;
    }
  }

  isFeatured(publisherId) {
    return (
      this.data.featured_publisher_ids &&
      this.data.featured_publisher_ids.includes(publisherId)
    );
  }

  isEditorsPick(publisherId) {
    return (
      this.data.editorial_pick_publisher_ids &&
      this.data.editorial_pick_publisher_ids.includes(publisherId)
    );
  }

  get isTicketed() {
    return this.data.sources.includes('geotix') || this.data['has_evvnt_ticketing?'];
  }

  isBasic(publisherId) {
    return !this.isTicketed && !this.isFeatured(publisherId);
  }

  get categoryNamesWithIds() {
    if (!this.data.category_names_with_ids) {
      return [];
    }

    return this.data.category_names_with_ids.map((category) => {
      const [id, name] = category.split('|');
      return { id, name };
    });
  }

  get categoryNames() {
    if (!this.data.category_names_with_ids) {
      return [this.data.category_name];
    }
    return [
      ...new Set(this.data.category_names_with_ids.map((category) => category.split('|')[1])),
    ];
  }

  get isEvvntEvent() {
    return this.mainSource === 'evvnt' || this.mainSource === 'geotix';
  }

  get isBackfill() {
    return !this.isEvvntEvent;
  }

  get href() {
    return `event/${this.slug}`;
  }

  get canonicalUrl() {
    return this.absoluteHref;
  }

  // WARNING: Non backfill events might not be present in the main calendar.
  get evvntHref() {
    return `${EVVNT_MAIN_CALENDAR_URL}?_evDiscoveryPath=/${this.href}`;
  }

  get absoluteHref() {
    // TODO - link to non-framed URL if publisherSettings tells us they have a subdomain version.
    return `${this.publisherSettings.publisher.url}?_evDiscoveryPath=/${encodeURIComponent(
      this.href
    )}`;
  }

  get slug() {
    return [
      this.urlId,
      this.title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-'),
    ].join('-');
  }

  get mainSource() {
    return this.data.sources[0];
  }

  get organizerName() {
    return this.data.organiser_name;
  }

  get ticketsLink() {
    if (this.data.links.Tickets) {
      return this.data.links.Tickets;
    }

    if (this.data.links.Booking) {
      return this.data.links.Booking;
    }
  }

  get websiteLink() {
    return this.data.links?.Website;
  }

  get brand() {
    if (['ticketweb', 'universe'].indexOf(this.data.sub_brand) !== -1) {
      return this.data.sub_brand;
    }

    return this.mainSource;
  }

  get isTicketmaster() {
    return this.mainSource === 'ticketmaster';
  }

  get mainLink() {
    const publisherId = this.publisherSettings.publisher.id;

    if (this.mainSource === 'ticketmaster' && this.ticketsLink) {
      const encodedPublisherName = encodeURIComponent(this.publisherSettings.publisher.name);

      if (this.ticketsLink.includes(EVVNT_TM_AFFILIATE_ID)) {
        return `${this.ticketsLink}&subid1=${publisherId}&sharedid=${encodedPublisherName}`;
      } else {
        return `https://ticketmaster.evyy.net/c/${EVVNT_TM_AFFILIATE_ID}/264167/4272?u=${encodeURIComponent(
          this.ticketsLink
        )}&subid1=${publisherId}&sharedid=${encodedPublisherName}`;
      }
    }

    if (this.mainSource === 'bandsintown' && this.data.links.Website) {
      return this.data.links.Website;
    }

    if (this.mainSource === 'meetup' && this.ticketsLink){
      const url = new URL(this.ticketsLink)

      url.searchParams.set('utm_campaign', publisherId)

      return url.toString()
    }

    if (this.ticketsLink) {
      return this.ticketsLink;
    }

    return Object.values(this.data.links)[0];
  }

  get youtubeUrl() {
    if (!this.data.original_links) {
      return;
    }

    const urls = Object.values(this.data.original_links).filter(
      (url) =>
        url.match(/^https?:\/\/[^\/]*youtube\.com\/watch/i) ||
        url.match(/^https?:\/\/youtu\.be/i)
    );
    if (urls.length) {
      return embeddedYoutubeUrl(urls[0]);
    }
  }

  get vimeoUrl() {
    if (!this.data.original_links) {
      return;
    }

    const urls = Object.values(this.data.original_links).filter((url) =>
      url.match(/^https?:\/\/[^\/]*vimeo\.com\/\d+/i)
    );

    if (urls.length) {
      return embeddedVimeoUrl(urls[0]);
    }
  }

  get videoUrl() {
    return this.youtubeUrl || this.vimeoUrl;
  }

  get urlId() {
    return new EventIds(this.data).id;
  }

  get contact() {
    if (this.data.contact && Object.keys(this.data.contact).length) {
      return this.data.contact;
    }
  }

  get externalMapUrl() {
    const mapSearchElements = [];
    const latlng = `${this.latitude},${this.longitude}`;

    if (this.data.venue.post_code) {
      mapSearchElements.push(this.data.venue.post_code);
    }
    if (this.data.venue.country) {
      mapSearchElements.push(this.data.venue.country);
    }
    if (this.data.venue.town) {
      mapSearchElements.push(this.data.venue.town);
    }
    if (this.data.venue.address_1) {
      mapSearchElements.push(this.data.venue.address_1);
    }
    if (this.venueName) {
      mapSearchElements.push(this.venueName);
    }
    if (this.latitude && this.longitude) {
      mapSearchElements.push(latlng);
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURI(
      mapSearchElements.join(', ')
    )}`;
  }

  metadata() {
    return {
      noindex: this.isExpired,
      title: this.title,
      description: this.description,
      canonicalURL: this.canonicalUrl,
      absoluteHref: this.absoluteHref,
      imageURL: this.fullImageUrl,
    };
  }

  structuredData() {
    return new StructuredData(this);
  }
}
